import { BackSide, Group, Mesh, Object3D, PlaneGeometry, Scene, ShaderMaterial, SphereGeometry, Vector3 } from 'three';
import type { Material, PerspectiveCamera, Texture, WebGLRenderer } from 'three';
import { SunLight } from 'three/examples/jsm/lights/SunLight.js';
import type { MapDef, QualityPreset } from '@cloudpearl/config';
import type { Sim } from '@cloudpearl/core';
import {
  SKY_FS,
  SKY_VS,
  doorway,
  invertedPeak,
  buildPrefabs,
  createDoorwayMaterials,
  createPeakMaterials,
  createBeamMaterial,
  createGlowMaterial,
  beamGeometry,
  createHorizonMaterial,
  horizonRing,
  createFoliageMaterials,
  createGateMaterial,
  createSolidMaterial,
  createTerrainMaterial,
  createWaterfallMaterial,
  windGateGeometry,
} from '@cloudpearl/assets';
import type { MaterialSlot, MaterialTextures, PrefabLibrary, SharedUniforms } from '@cloudpearl/assets';
import type { BufferGeometry } from 'three';
import { TileStreamer } from './TileStreamer';
import { Terrain } from './Terrain';
import { LocalHeightfield } from './LocalHeightfield';
import { Grass } from './Grass';
import type { GroundRange } from './Grass';
import { Props } from './Props';
import { AmbientParticles, Bursts } from './Particles';
import { TrailMap } from './TrailMap';
import { EnvProbe } from './EnvProbe';
import { Butterflies } from './Butterflies';

/**
 * Everything visible in a map: sky, sun & cascaded shadows, streamed terrain tiles and island
 * undersides, GPU grass, instanced scenery and wind gates, particles. Owns no game logic — it
 * renders whatever the sim's deterministic world says.
 */
export class World {
  readonly scene = new Scene();
  readonly map: MapDef;
  readonly sun: SunLight;
  readonly bursts: Bursts;
  readonly ambient: AmbientParticles;
  readonly heightfield: LocalHeightfield;
  private readonly groundRange: GroundRange;
  readonly trail = new TrailMap();
  readonly probe: EnvProbe | null;
  private readonly butterflies: Butterflies | null;
  private readonly beacon: Group;
  private readonly landmark: 'beam' | 'peak' | 'door';
  /** On the menu: the pearl, which butterflies keep clear of (null in play). */
  menuPearl: Vector3 | null = null;
  private landGlow: Mesh | null = null;
  private fallHalo: Mesh | null = null;
  private readonly beam: Mesh;
  private readonly horizon: { mesh: Mesh; base: number }[] = [];
  private readonly shared: SharedUniforms;
  private readonly sky: Mesh;
  private readonly materials = new Map<MaterialSlot, Material>();
  private readonly foliageDepth: Material;
  private readonly terrainMat: ShaderMaterial;
  private readonly gateMat: ShaderMaterial;
  private readonly gateGeo: BufferGeometry;
  private readonly prefabs: PrefabLibrary;
  private readonly root = new Object3D();
  private readonly streamer: TileStreamer;
  private readonly terrain: Terrain;
  private readonly grass: Grass;
  private readonly props: Props;
  private sim: Sim;
  private streamTimer = 0;
  private probeTick = 0;
  private readonly probeHide: Object3D[] = [];
  private readonly lastStream = new Vector3(1e9, 0, 0);

  constructor(map: MapDef, sim: Sim, shared: SharedUniforms, quality: QualityPreset, textures: MaterialTextures) {
    this.map = map;
    this.sim = sim;
    this.shared = shared;
    this.scene.add(this.root);
    this.root.matrixAutoUpdate = false;

    // Sun: cascaded shadow maps fitted to the view (SunLight). Our shaders do their own lighting
    // and only read its shadows; physically based surfaces (the pearl) are lit by it directly.
    this.sun = new SunLight(0xffffff, 0);
    this.sun.castShadow = quality.shadows;
    this.sun.shadow.mapSize.set(quality.shadowMapSize, quality.shadowMapSize);
    this.sun.shadow.camera.far = quality.shadowDistance;
    this.sun.shadow.camera.near = 0.5;
    this.sun.shadow.bias = -0.0006;
    this.sun.shadow.normalBias = 0.55;
    this.sun.shadow.radius = 3.5;
    this.scene.add(this.sun);

    // Sky dome.
    const skyMat = new ShaderMaterial({
      uniforms: { ...shared.all(), uCloudBand: { value: map.clouds.skyBand }, uSunDisk: { value: 1 } },
      vertexShader: SKY_VS,
      fragmentShader: SKY_FS,
      side: BackSide,
      depthWrite: false,
    });
    this.sky = new Mesh(new SphereGeometry(1, 32, 16), skyMat);
    this.sky.scale.setScalar(2600);
    // Drawn after every opaque surface: only the pixels still uncovered run the sky shader
    // (first, it shaded the whole screen and was then mostly painted over).
    this.sky.renderOrder = 1000;
    this.sky.frustumCulled = false;
    this.scene.add(this.sky);

    this.heightfield = new LocalHeightfield();
    this.groundRange = (x0, z0, x1, z1, out) => this.heightfield.groundRange(x0, z0, x1, z1, out);
    this.grass = new Grass(this.root, shared, map, quality, this.heightfield.texture);

    // Materials.
    this.terrainMat = createTerrainMaterial(shared, map, textures, this.grass.outer || 60);
    const cliff = createSolidMaterial(shared, map, textures, 'cliff');
    const fol = createFoliageMaterials(shared, map, textures, quality.msaa > 0);
    this.foliageDepth = fol.depth;
    const slots: [MaterialSlot, Material][] = [
      ['stone', createSolidMaterial(shared, map, textures, 'stone')],
      ['mossStone', createSolidMaterial(shared, map, textures, 'mossStone')],
      ['sandstone', createSolidMaterial(shared, map, textures, 'sandstone')],
      ['cliff', cliff],
      ['bark', createSolidMaterial(shared, map, textures, 'bark')],
      ['mossWood', createSolidMaterial(shared, map, textures, 'mossWood')],
      ['plaster', createSolidMaterial(shared, map, textures, 'plaster')],
      ['roof', createSolidMaterial(shared, map, textures, 'roof')],
      ['cactus', createSolidMaterial(shared, map, textures, 'cactus')],
      ['grassTop', createSolidMaterial(shared, map, textures, 'grassTop')],
      ['tuft', createSolidMaterial(shared, map, textures, 'tuft')],
      ['foliage', fol.material],
      ['waterfall', createWaterfallMaterial(shared, map)],
    ];
    for (const [k, m] of slots) this.materials.set(k, m);
    this.gateMat = createGateMaterial(shared, map);
    this.gateGeo = windGateGeometry();

    this.prefabs = buildPrefabs(map);
    this.props = new Props(this.root, this.prefabs, this.materials, this.foliageDepth, this.gateGeo, this.gateMat, sim, quality);

    this.streamer = new TileStreamer(sim.world.seed, map.id);
    this.terrain = new Terrain(this.root, this.terrainMat, cliff as ShaderMaterial, this.streamer, this.heightfield, quality, sim.world.islands, this.grass.outer);

    this.bursts = new Bursts(this.scene, shared, this.heightfield.texture, Math.round(1600 * quality.particles) + 400);
    this.ambient = new AmbientParticles(this.scene, shared, map, Math.round(map.wind.particleCount * quality.particles), this.heightfield.texture);

    // The pearl's trail, sampled by sand and grass shaders.
    shared.tTrail.value = this.trail.texture;
    shared.uTrailInfo.value.set(this.trail.texelSize, this.trail.windowSize, 1, 0);

    // The far-north landmark, always due north on the horizon — you can run toward it forever and
    // never arrive. (`?landmark=door|peak` overrides the map's choice, for comparing.)
    this.beacon = new Group();
    const b = map.atmosphere.beacon;
    const override = new URLSearchParams(location.search).get('landmark');
    this.landmark = override === 'door' || override === 'peak' || override === 'beam' ? override : b.kind;
    if (this.landmark === 'beam') {
      // A signal: one thin, clean beam of light standing on the ground at the horizon, straight
      // up into the sky (the original beacon beam, now planted on the ground).
      this.beam = new Mesh(beamGeometry(), createBeamMaterial(shared, b.beam, 0.002));
      this.beam.scale.set(4, 2600, 4);
      this.beam.renderOrder = 4;
      // The faintest glow where it meets the ground: the light has a source.
      const root = new Mesh(new PlaneGeometry(2, 2), createGlowMaterial(b.beam));
      (root.material as ShaderMaterial).uniforms['uSize']!.value.set(26, 9);
      (root.material as ShaderMaterial).uniforms['uStrength']!.value = 0.45;
      root.position.y = 3;
      root.renderOrder = 5;
      for (const m of [this.beam, root]) m.frustumCulled = false;
      this.beacon.add(this.beam, root);
    } else if (this.landmark === 'peak') {
      // An inverted peak hanging over the horizon, light falling from its tip to the ground.
      const peak = invertedPeak(b.height * 0.38);
      const pm = createPeakMaterials(shared, map);
      const rock = new Mesh(peak.rock, pm.rock);
      rock.renderOrder = 900;
      this.beam = new Mesh(peak.fall, pm.fall);
      this.beam.renderOrder = 4;
      const r = b.height * 0.011;
      this.beam.scale.set(r, 1, r);
      this.fallHalo = new Mesh(peak.fall, pm.halo);
      this.fallHalo.renderOrder = 4;
      this.fallHalo.scale.set(r * 2.6, 1, r * 2.6);
      const tipGlow = new Mesh(new PlaneGeometry(2, 2), pm.glow());
      (tipGlow.material as ShaderMaterial).uniforms['uSize']!.value.set(b.height * 0.09, b.height * 0.09);
      (tipGlow.material as ShaderMaterial).uniforms['uStrength']!.value = 0.9;
      tipGlow.renderOrder = 5;
      this.landGlow = new Mesh(new PlaneGeometry(2, 2), pm.glow());
      (this.landGlow.material as ShaderMaterial).uniforms['uSize']!.value.set(b.height * 0.16, b.height * 0.035);
      (this.landGlow.material as ShaderMaterial).uniforms['uStrength']!.value = 0.55;
      this.landGlow.renderOrder = 5;
      for (const m of [rock, this.beam, this.fallHalo, tipGlow, this.landGlow]) m.frustumCulled = false;
      this.beacon.add(rock, this.beam, this.fallHalo, tipGlow, this.landGlow);
    } else {
      // A lone, enormous doorway with light pouring through it.
      const door = doorway(b.height * 0.62);
      const dm = createDoorwayMaterials(shared, map, door);
      const stone = new Mesh(door.stone, dm.stone);
      stone.renderOrder = 900;
      const light = new Mesh(door.light, dm.light);
      light.renderOrder = 901;
      this.beam = new Mesh(door.spill, dm.spill);
      this.beam.renderOrder = 4;
      for (const m of [stone, light, this.beam]) m.frustumCulled = false;
      this.beacon.add(stone, light, this.beam);
    }
    this.scene.add(this.beacon);

    // Layered horizon: distant cloud banks / far dune ridges, deep in the haze.
    const hz = map.atmosphere.horizon;
    const layers = hz.kind === 'clouds'
      ? [{ r: 1850, h: hz.height, base: -170, haze: 0.5, seed: 1.3 }, { r: 2500, h: hz.height * 1.35, base: -220, haze: 0.68, seed: 4.1 }]
      : [{ r: 1350, h: hz.height * 0.6, base: -22, haze: 0.5, seed: 2.2 }, { r: 2100, h: hz.height, base: -34, haze: 0.66, seed: 5.7 }];
    for (const l of layers) {
      const m = new Mesh(horizonRing(l.r, l.h, hz.kind, l.seed), createHorizonMaterial(shared, map, l.haze));
      m.frustumCulled = false;
      // Far and mostly hidden behind the land: drawn late so covered pixels are never shaded.
      m.renderOrder = 900;
      this.horizon.push({ mesh: m, base: l.base });
      this.scene.add(m);
    }

    // Butterflies over the meadow.
    const nb = quality.level === 'high' ? 14 : quality.level === 'medium' ? 8 : 0;
    this.butterflies = map.grass.enabled && nb > 0 ? new Butterflies(this.scene, shared, this.heightfield.texture, nb) : null;

    // Live reflections for the pearl (not on low).
    this.probe = quality.level === 'low' ? null : new EnvProbe(quality.level === 'high' ? 128 : 64);
  }

  /**
   * Before the shader warm-up: show every prop pool (empty ones are normally hidden) and render
   * the whole reflection probe once, so nothing compiles mid-run. Call again with `false` after.
   */
  warmup(on: boolean, gl?: WebGLRenderer, center?: Vector3, hide?: Object3D): void {
    this.props.warmup(on);
    if (on && this.probe && gl && center && hide) {
      for (let f = 0; f < 6; f++) {
        this.probeTick = 2; // the next tick renders a face
        this.renderProbe(gl, center, hide);
      }
    }
  }

  /** Re-render part of the pearl's reflection probe (call once per frame before rendering). */
  renderProbe(gl: WebGLRenderer, center: Vector3, hide: Object3D): void {
    if (!this.probe) return;
    // One face every third frame: a full refresh ~3× a second is plenty for a reflection on a
    // ball a few hundred pixels across.
    this.probeTick = (this.probeTick + 1) % 3;
    if (this.probeTick !== 0 && this.probe.ready) return;
    const list = this.probeHide;
    list.length = 0;
    list.push(this.grass.group, this.bursts.group, this.ambient.mesh, this.beam, hide);
    if (this.butterflies) list.push(this.butterflies.mesh);
    this.probe.update(gl, this.scene, center, list, 1);
  }

  /** New seed on the same map: drop streamed content, keep materials/prefabs. */
  setSim(sim: Sim): void {
    if (sim.world === this.sim.world) {
      this.sim = sim;
      return;
    }
    this.sim = sim;
    this.terrain.clear();
    this.heightfield.clear();
    this.trail.clear();
    this.props.setSim(sim);
    this.streamer.reset(sim.world.seed, this.map.id);
    this.lastStream.set(1e9, 0, 0);
  }

  /** True once the area around (x, z) is on screen (holds the loading veil). */
  readyAround(x: number, z: number): boolean {
    return this.terrain.ready(x, z, 200);
  }

  setEnvironment(env: Texture | null): void {
    this.scene.environment = env;
  }

  /** Stream around the camera/player, follow the sun/sky/particles. */
  update(camera: PerspectiveCamera, focus: Vector3, forward: Vector3, dt: number): void {
    const cam = camera.position;
    // Stream around a point a little ahead of the player (what you're about to see).
    const sx = focus.x + forward.x * 40;
    const sz = focus.z + forward.z * 40;
    this.streamTimer -= dt;
    if (this.streamTimer <= 0 || Math.hypot(focus.x - this.lastStream.x, focus.z - this.lastStream.z) > 12) {
      this.streamTimer = 0.2;
      this.lastStream.set(focus.x, 0, focus.z);
      this.streamer.beginRequests();
      this.terrain.update(focus.x, focus.z, sx, sz);
    }
    for (const r of this.streamer.pump()) {
      if (r.kind === 'tile') this.terrain.addTile(r.data);
      else this.terrain.addUndersides(r.ti, r.tj, r.list);
    }
    this.props.update(sx, sz, dt);
    this.heightfield.update(cam.x, cam.z);
    this.grass.update(camera, this.groundRange);
    this.bursts.flush();

    // The SunLight shines from its position toward the origin; cascades fit the view camera.
    this.sun.position.copy(this.shared.uSunDir.value).multiplyScalar(100);
    this.sun.color.copy(this.shared.uSunColor.value);
    this.sun.intensity = 1;
    this.sun.updateMatrixWorld();

    this.sky.position.copy(cam);
    this.ambient.follow(cam, forward);
    this.butterflies?.follow(cam, forward);
    this.butterflies?.keepClear(cam, this.menuPearl, forward, dt);

    this.trail.recenter(focus.x, focus.z);
    this.trail.flush();
    this.shared.uTrailWin.value.set(this.trail.centerX, this.trail.centerZ, this.trail.windowSize / 2, 0);

    // The landmark: pinned to the northern horizon (moves with the camera, so it never gets closer).
    const dist = 2350;
    // Ground level out there: the cloud sea / the far dunes.
    const base = this.map.terrain.islands.enabled ? -140 : 10;
    if (this.landmark === 'beam') {
      // Just in front of the nearer horizon ridges, so its foot is seen standing on the ground
      // (on Sky Isles, rising out of the cloud sea).
      const isl = this.map.terrain.islands.enabled;
      this.beacon.position.set(cam.x, isl ? -150 : 4, cam.z - (isl ? 1780 : 1250));
    } else if (this.landmark === 'peak') {
      // Hovering: the tip well above the ground, the light falling the whole way down (on Sky
      // Isles, past the islands into the cloud sea far below).
      const hover = this.map.atmosphere.beacon.height * (this.map.terrain.islands.enabled ? 0.48 : 0.27);
      this.beacon.position.set(cam.x, base + hover, cam.z - dist);
      this.beam.scale.y = hover + 30;
      if (this.fallHalo) this.fallHalo.scale.y = hover + 30;
      this.landGlow?.position.set(0, -hover + 6, 40);
    } else this.beacon.position.set(cam.x, base, cam.z - dist);
    for (const h of this.horizon) h.mesh.position.set(cam.x, h.base, cam.z);
  }

  /** Every material used by the world, for shader pre-compilation. */
  get allMaterials(): Material[] {
    return [this.terrainMat, this.gateMat, ...this.beacon.children.map((c) => (c as Mesh).material as Material), ...this.horizon.map((h) => h.mesh.material as Material), ...this.materials.values(), ...this.grass.allMaterials];
  }

  get stats(): string {
    const t = this.terrain.stats;
    return `tiles ${t.tiles} isl ${t.undersides} q ${this.streamer.pending}`;
  }

  dispose(): void {
    this.terrain.dispose();
    this.streamer.dispose();
    this.props.dispose();
    this.grass.dispose();
    for (const variants of this.prefabs.values()) {
      for (const v of variants) {
        for (const part of v.parts) {
          part.geometry.dispose();
          part.far?.dispose();
        }
      }
    }
    for (const m of this.allMaterials) m.dispose();
    this.foliageDepth.dispose();
    this.gateGeo.dispose();
    this.beacon.traverse((o) => (o as Mesh).geometry?.dispose());
    for (const h of this.horizon) h.mesh.geometry.dispose();
    this.trail.dispose();
    this.probe?.dispose();
    this.shared.tTrail.value = null;
    this.bursts.dispose();
    this.ambient.dispose();
    this.butterflies?.dispose();
    this.heightfield.dispose();
    this.sky.geometry.dispose();
    (this.sky.material as Material).dispose();
    this.sun.shadow.dispose();
  }
}
